/* eslint-disable max-len */
import { Card, Col, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import TooltipComponent from 'components/atoms/TooltipComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import { ETypPrevodu } from 'services/specialniSluzby/uePozadavky';
import uePrehledApi, { IPrehled, IPrehledPozadavek } from 'services/specialniSluzby/uePrehled';

import routePathEnum from 'enums/routePathEnum';
import { EStavPozadavku } from 'enums/specialniSluzby/stavPozadavku';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';

const PrehledSpecialniSluzby: React.FC = () => {
    const { aktivniFirma, language } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<IPrehled>();
    const { setLevel } = useProfily();

    useEffect(() => {
        setLoading(true);
        setLevel('specialniSluzby', null);
        setLevel('specialniSluzbyDVP', null);
        uePrehledApi({
            aktivni: true
        })
            .then((resp: IPrehled) => {
                setData(resp);
            })
            .catch((err: any) => setErrStatus(err.response?.status))
            .finally(() => {
                setLoading(false);
                setErrStatus(0);
            });
    }, [setLevel]);

    return (
        <LoadingWrapper loading={loading}>
            <Title level={4} text="SS_PREHLED_AKTIVNICH_POZADAVKU" />
            {errStatus ? (
                <Row>
                    <Col span={16}>
                        {errStatus === 404 ? (
                            <Paragraph text="RESPONSE_ERROR_404" />
                        ) : errStatus ? (
                            <Paragraph text="SLUZBA_NEDOSTUPNA" />
                        ) : null}
                        <Row className="button-row">
                            <ButtonComponent
                                id="zpetBtn"
                                type="default"
                                buttontext="ZPET"
                                onClick={() => {
                                    setErrStatus(0);
                                }}
                            />
                        </Row>
                    </Col>
                </Row>
            ) : (
                <>
                    {!data || !data.pozadavky.length ? (
                        <Row
                            gutter={8}
                            style={{
                                height: '50vh'
                            }}
                        >
                            <Title text="ZADNA_DATA" level={3} />
                        </Row>
                    ) : (
                        <Row>
                            {data.pozadavky.map((item: IPrehledPozadavek, index: number) => {
                                return (
                                    <Col
                                        key={item.id}
                                        span={12}
                                        style={{
                                            paddingBottom: '10px',
                                            paddingRight: '10px'
                                        }}
                                    >
                                        <Card
                                            className="dash-card"
                                            title={
                                                <>
                                                    <Paragraph
                                                        style={{
                                                            fontSize: 20
                                                        }}
                                                        text={'SS_PREHLED_POZADAVEK_' + item.typ}
                                                    />
                                                </>
                                            }
                                        >
                                            <Row>
                                                <Col span={12}>
                                                    <div>
                                                        <Paragraph text="ISIN" strong={true} />
                                                    </div>
                                                    <div>
                                                        <Paragraph text={item.isin || ''} />
                                                    </div>
                                                </Col>
                                                <Col span={12}>
                                                    <div>
                                                        <Paragraph text="POCET_CP" strong={true} />
                                                    </div>
                                                    <div>
                                                        <Paragraph
                                                            text={'' + formatInputNumberThousand(item.pocetcp || '0')}
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row
                                                style={{
                                                    paddingTop: '10px'
                                                }}
                                            >
                                                <Col span={12}>
                                                    <div>
                                                        <Paragraph
                                                            text={`${getLanguageObject(language).DATUM} 
                                                                ${
                                                                    item.typ
                                                                        ? getLanguageObject(language)[
                                                                              'SS_DATUM_' + item.typ.toUpperCase() || ''
                                                                          ]
                                                                        : ''
                                                                }
                                                            `}
                                                            strong={true}
                                                        />
                                                    </div>
                                                    <div>
                                                        <Paragraph text={dayjs(item.datum).format('DD.MM.YYYY')} />
                                                    </div>
                                                </Col>
                                                {!!item.pocetuctu && (
                                                    <Col span={12}>
                                                        <div>
                                                            <Paragraph text="SS_PREHLED_POCET_UCTU" strong={true} />
                                                        </div>
                                                        <div>{formatInputNumberThousand(item.pocetuctu || 0)}</div>
                                                    </Col>
                                                )}
                                            </Row>

                                            <Row>
                                                {item.meznidatum && (
                                                    <Col span={12}>
                                                        <div>
                                                            <Paragraph
                                                                text="LZE_ODESLAT"
                                                                strong={true}
                                                                style={{
                                                                    marginRight: 10
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Paragraph
                                                                text={
                                                                    dayjs(item.meznidatum).format('DD.MM.YYYY') +
                                                                    (item.typprevodu === ETypPrevodu.PROTIZAPLACENI ||
                                                                    item.typprevodu === ETypPrevodu.PROTIABEZZAPL
                                                                        ? ' 11:00'
                                                                        : ' 16:30')
                                                                }
                                                            />
                                                        </div>
                                                    </Col>
                                                )}
                                                {item.stav !== EStavPozadavku.VYRIZENO ? (
                                                    <>
                                                        {!!item.kontrolauctu && (
                                                            <Col span={12}>
                                                                <div>
                                                                    <Paragraph text="KONTROLA" strong={true} />
                                                                </div>
                                                                <div>
                                                                    {formatInputNumberThousand(item.kontrolauctu || 0)}
                                                                </div>
                                                            </Col>
                                                        )}
                                                        {!!item.zaevidovanecp && (
                                                            <Col span={12} offset={item.typtransakce ? 0 : 12}>
                                                                <div>
                                                                    <Paragraph text="ZAEVIDOVANE_CP" strong={true} />
                                                                </div>
                                                                <div>
                                                                    {formatInputNumberThousand(item.zaevidovanecp || 0)}
                                                                </div>
                                                            </Col>
                                                        )}
                                                    </>
                                                ) : (
                                                    !!item.vyporadanecp && (
                                                        <Col span={12}>
                                                            <div>
                                                                <Paragraph text="UPSANE_CP" strong={true} />
                                                            </div>
                                                            <div>
                                                                {formatInputNumberThousand(item.vyporadanecp || 0)}
                                                            </div>
                                                        </Col>
                                                    )
                                                )}
                                            </Row>

                                            <Row>
                                                <Col span={12}>
                                                    <div>
                                                        <Paragraph text="STAV" strong={true} />
                                                    </div>
                                                    <div>
                                                        {item.stav === EStavPozadavku.VYRIZENO ? (
                                                            <TranslateText>SS_PREHLED_STAV_VYRIZENO</TranslateText>
                                                        ) : item.stav === EStavPozadavku.OTEVRENO ? (
                                                            <TranslateText>SS_PREHLED_STAV_LZE_PRIDAVAT</TranslateText>
                                                        ) : (
                                                            <TranslateText>
                                                                SS_PREHLED_STAV_NELZE_PRIDAVAT
                                                            </TranslateText>
                                                        )}
                                                        {item.sankce && (
                                                            <TooltipComponent title="SS_SANKCE_TOOLTIP">
                                                                <p
                                                                    style={{
                                                                        color: 'red',
                                                                        fontWeight: 'bold',
                                                                        marginLeft: 10
                                                                    }}
                                                                >
                                                                    !
                                                                </p>
                                                            </TooltipComponent>
                                                        )}
                                                    </div>
                                                </Col>
                                                {item.stav !== EStavPozadavku.VYRIZENO && !!item.zaevidovaneucty && (
                                                    <Col span={12}>
                                                        <div>
                                                            <Paragraph text="ZAEVIDOVANE_UCTY" strong={true} />
                                                        </div>
                                                        <div>
                                                            {formatInputNumberThousand(item.zaevidovaneucty || 0)}
                                                        </div>
                                                    </Col>
                                                )}
                                                {item.typprevodu === ETypPrevodu.PROTIZAPLACENI &&
                                                (item.neuhrazenecp || item.neuhrazeneucty) ? (
                                                    <Col span={12}>
                                                        <div>
                                                            <Paragraph text="POCET_CP_NEUHRAZENE" strong={true} />
                                                        </div>
                                                        <div>
                                                            {(item.neuhrazenecp || 0) +
                                                                ' / ' +
                                                                (item.neuhrazeneucty || 0)}
                                                        </div>
                                                    </Col>
                                                ) : (
                                                    item.stav === EStavPozadavku.VYRIZENO &&
                                                    !!item.nevyporadanecp && (
                                                        <Col span={12}>
                                                            <div>
                                                                <Paragraph text="NEUPSANE_CP" strong={true} />
                                                            </div>
                                                            <div>
                                                                {formatInputNumberThousand(item.nevyporadanecp || 0)}
                                                            </div>
                                                        </Col>
                                                    )
                                                )}
                                            </Row>

                                            <Row className="button-row">
                                                <Col span={12}>
                                                    <ButtonComponent
                                                        id={`zobrazitDetailBtn${index}`}
                                                        buttontext={'ZOBRAZIT_DETAIL'}
                                                        onClick={() => {
                                                            setLevel('specialniSluzby', {
                                                                detail: {
                                                                    ...item
                                                                }
                                                            });

                                                            navigateWorkaround(
                                                                // eslint-disable-next-line max-len
                                                                `/firma/${aktivniFirma?.iducet}${
                                                                    item.dlt
                                                                        ? routePathEnum.SLUZBY_DETAIL_POZADAVKU_DLT
                                                                        : routePathEnum.SLUZBY_DETAIL_POZADAVKU
                                                                }`
                                                            );
                                                        }}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                </>
            )}
        </LoadingWrapper>
    );
};

export default PrehledSpecialniSluzby;
